/**
 * MessageGroupManager - Gerenciador de múltiplos grupos de mensagens para campanhas
 * 
 * Permite criar e gerenciar múltiplos grupos de mensagens, cada um com seu próprio
 * conteúdo (textos, mídias, delays). Sistema usa seleção aleatória ponderada
 * baseada no peso de cada grupo.
 * 
 * @author Sistema de Campanhas v3.0
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import {
  Plus,
  Trash2,
  Copy,
  ChevronDown,
  ChevronUp,
  Layers,
  AlertCircle,
  ImageIcon,
} from 'lucide-react';
import { CampaignMessageBuilder } from './CampaignMessageBuilder';
import { MessageItem } from './types';
import { MediaGalleryDialog } from '../media/MediaGalleryDialog';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

/**
 * Interface para um grupo de mensagens
 */
export interface MessageGroup {
  id: string;
  name: string;
  items: MessageItem[];
  weight: number; // Peso para seleção aleatória (1-100)
}

interface MessageGroupManagerProps {
  groups: MessageGroup[];
  onGroupsChange: (groups: MessageGroup[]) => void;
  availableVariables?: string[];
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function MessageGroupManager({
  groups,
  onGroupsChange,
  availableVariables = [],
}: MessageGroupManagerProps) {
  // Estado do grupo ativo (expandido para edição)
  const [activeGroupId, setActiveGroupId] = useState<string | null>(
    groups.length > 0 ? groups[0].id : null
  );
  
  // Estado do modal de galeria de mídia
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false
  );

  // Atualizar grupo ativo quando grupos mudam
  useEffect(() => {
    if (groups.length > 0 && !groups.find(g => g.id === activeGroupId)) {
      setActiveGroupId(groups[0].id);
    }
  }, [groups, activeGroupId]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  /**
   * Adiciona um novo grupo de mensagens
   */
  const handleAddGroup = () => {
    const newGroup: MessageGroup = {
      id: crypto.randomUUID(),
      name: `Grupo ${groups.length + 1}`,
      items: [],
      weight: 100,
    };
    onGroupsChange([...groups, newGroup]);
    setActiveGroupId(newGroup.id);
  };

  /**
   * Remove um grupo de mensagens
   */
  const handleRemoveGroup = (groupId: string) => {
    if (groups.length <= 1) {
      return; // Manter pelo menos 1 grupo
    }
    const newGroups = groups.filter(g => g.id !== groupId);
    onGroupsChange(newGroups);
    if (activeGroupId === groupId) {
      setActiveGroupId(newGroups[0]?.id || null);
    }
  };

  /**
   * Duplica um grupo de mensagens
   */
  const handleDuplicateGroup = (groupId: string) => {
    const groupToDuplicate = groups.find(g => g.id === groupId);
    if (!groupToDuplicate) return;

    const duplicatedGroup: MessageGroup = {
      id: crypto.randomUUID(),
      name: `${groupToDuplicate.name} (cópia)`,
      items: groupToDuplicate.items.map(item => ({
        ...item,
        id: crypto.randomUUID(),
      })),
      weight: groupToDuplicate.weight,
    };

    const groupIndex = groups.findIndex(g => g.id === groupId);
    const newGroups = [...groups];
    newGroups.splice(groupIndex + 1, 0, duplicatedGroup);
    onGroupsChange(newGroups);
    setActiveGroupId(duplicatedGroup.id);
  };

  /**
   * Atualiza o nome de um grupo
   */
  const handleGroupNameChange = (groupId: string, name: string) => {
    onGroupsChange(
      groups.map(g => (g.id === groupId ? { ...g, name } : g))
    );
  };

  /**
   * Atualiza o peso de um grupo
   */
  const handleGroupWeightChange = (groupId: string, weight: number) => {
    onGroupsChange(
      groups.map(g => (g.id === groupId ? { ...g, weight } : g))
    );
  };

  /**
   * Atualiza os itens de mensagem de um grupo
   */
  const handleGroupItemsChange = (groupId: string, items: MessageItem[]) => {
    onGroupsChange(
      groups.map(g => (g.id === groupId ? { ...g, items } : g))
    );
  };

  /**
   * Move um grupo para cima na lista
   */
  const handleMoveUp = (groupId: string) => {
    const index = groups.findIndex(g => g.id === groupId);
    if (index <= 0) return;
    
    const newGroups = [...groups];
    [newGroups[index - 1], newGroups[index]] = [newGroups[index], newGroups[index - 1]];
    onGroupsChange(newGroups);
  };

  /**
   * Move um grupo para baixo na lista
   */
  const handleMoveDown = (groupId: string) => {
    const index = groups.findIndex(g => g.id === groupId);
    if (index >= groups.length - 1) return;
    
    const newGroups = [...groups];
    [newGroups[index], newGroups[index + 1]] = [newGroups[index + 1], newGroups[index]];
    onGroupsChange(newGroups);
  };

  // ---------------------------------------------------------------------------
  // Cálculos
  // ---------------------------------------------------------------------------

  /**
   * Calcula a porcentagem real de cada grupo baseado nos pesos
   */
  const totalWeight = groups.reduce((sum, g) => sum + g.weight, 0);
  const getGroupPercentage = (weight: number) => {
    if (totalWeight === 0) return 0;
    return Math.round((weight / totalWeight) * 100);
  };

  // Grupo ativo para edição
  const activeGroup = groups.find(g => g.id === activeGroupId);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* Header com botão de adicionar grupo */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-medium">Grupos de Mensagens</h3>
          <Badge variant="secondary" className="text-xs">
            {groups.length} {groups.length === 1 ? 'grupo' : 'grupos'}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {/* Botão para abrir galeria de mídia (upload de arquivos) */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setGalleryDialogOpen(true)}
            className="gap-2"
          >
            <ImageIcon className="h-4 w-4" />
            Galeria
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddGroup}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Adicionar Grupo
          </Button>
        </div>
      </div>

      {/* Aviso sobre múltiplos grupos */}
      {groups.length > 1 && (
        <div className="flex items-start gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg text-sm">
          <AlertCircle className="h-4 w-4 mt-0.5 text-primary shrink-0" />
          <p className="text-muted-foreground">
            <span className="font-medium text-foreground">Diversificação ativa:</span>{' '}
            O sistema selecionará aleatoriamente um grupo para cada destinatário, 
            usando os pesos configurados para distribuição ponderada.
          </p>
        </div>
      )}

      {/* Lista de grupos — cada grupo é um "cockpit dark studio" com header próprio */}
      <div className="space-y-3">
        {groups.map((group, index) => {
          const isActive = group.id === activeGroupId;
          const percentage = getGroupPercentage(group.weight);
          const indexLabel = String(index + 1).padStart(2, '0');

          return (
            <div
              key={group.id}
              className={cn(
                "rounded-2xl overflow-hidden transition-all bg-white border shadow-lg shadow-black/5",
                isActive
                  ? "border-indigo-500/40 ring-1 ring-indigo-500/20"
                  : "border-zinc-200 hover:border-zinc-300"
              )}
            >
              {/* ====== Header do grupo (Control Bar) ====== */}
              <div
                className="flex flex-wrap items-center gap-4 px-5 py-3.5 bg-white border-b border-zinc-200 cursor-pointer"
                onClick={() => setActiveGroupId(isActive ? null : group.id)}
              >
                {/* Identidade do grupo (badge #, dot, nome editável, contador) */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="font-mono text-[11px] text-indigo-500 bg-indigo-500/10 px-2 py-1 rounded-md border border-indigo-500/20 shrink-0">
                    #{indexLabel}
                  </span>

                  {/* Dot status com glow (verde quando tem itens) */}
                  <div className="relative flex items-center shrink-0" aria-hidden="true">
                    <div
                      className={cn(
                        "w-2 h-2 rounded-full",
                        group.items.length > 0 ? "bg-emerald-500 animate-pulse" : "bg-zinc-300"
                      )}
                    />
                    {group.items.length > 0 && (
                      <div className="absolute w-2 h-2 bg-emerald-500 rounded-full opacity-50 blur-sm" />
                    )}
                  </div>

                  {/* Nome editável inline */}
                  <Input
                    value={group.name}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleGroupNameChange(group.id, e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-7 px-2 text-sm font-semibold bg-transparent border border-transparent text-zinc-900 hover:border-zinc-300 focus:border-indigo-500 focus-visible:ring-1 focus-visible:ring-indigo-500/30 max-w-[260px]"
                  />

                  {/* Contador de itens */}
                  <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded shrink-0">
                    {group.items.length} {group.items.length === 1 ? 'item' : 'itens'}
                  </span>
                </div>

                {/* Slider de peso compacto */}
                <div
                  className="flex flex-col items-end gap-1 w-44 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between w-full text-[10px] font-bold uppercase tracking-tighter">
                    <span className="text-zinc-400">Peso</span>
                    <span className="text-indigo-500">
                      {group.weight} ({percentage}%)
                    </span>
                  </div>
                  <Slider
                    value={[group.weight]}
                    onValueChange={([value]) => handleGroupWeightChange(group.id, value)}
                    min={1}
                    max={100}
                    step={1}
                    className="w-full"
                  />
                </div>

                {/* Pill de ações (mover/duplicar/excluir) */}
                <div
                  className="flex items-center bg-zinc-100 p-1 rounded-lg border border-zinc-200 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200"
                    onClick={() => handleMoveUp(group.id)}
                    disabled={index === 0}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200"
                    onClick={() => handleMoveDown(group.id)}
                    disabled={index === groups.length - 1}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <div className="w-px h-4 bg-zinc-300 mx-1" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200"
                    onClick={() => handleDuplicateGroup(group.id)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-rose-500 hover:text-rose-600 hover:bg-rose-50"
                    onClick={() => handleRemoveGroup(group.id)}
                    disabled={groups.length <= 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* ====== Conteúdo expandido — Editor (dock + timeline) ====== */}
              {isActive && (
                <div className="bg-zinc-50/50">
                  <CampaignMessageBuilder
                    items={group.items}
                    onItemsChange={(items) => handleGroupItemsChange(group.id, items)}
                    availableVariables={availableVariables}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Resumo de distribuição */}
      {groups.length > 1 && (
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-xs font-medium mb-2 text-muted-foreground">
            Distribuição estimada:
          </p>
          <div className="flex flex-wrap gap-2">
            {groups.map(group => (
              <Badge key={group.id} variant="outline" className="text-xs">
                {group.name}: {getGroupPercentage(group.weight)}%
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Modal de galeria de mídia para upload de arquivos */}
      <MediaGalleryDialog
        open={galleryDialogOpen}
        onOpenChange={setGalleryDialogOpen}
        selectionMode={false}
      />
    </div>
  );
}
